// src/components/TechBackground.js

import React from 'react';

export default function TechBackground() {
    return (
        <div className="fixed inset-0 z-0 bg-slate-950 overflow-hidden select-none pointer-events-none">
            {/* Background Base */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,#0f172a_0%,#020617_100%)]"></div>
            
            {/* Fine Grid Pattern */}
            <div className="absolute inset-0 opacity-10" style={{ 
                backgroundImage: 'linear-gradient(#22d3ee 1px, transparent 1px), linear-gradient(90deg, #22d3ee 1px, transparent 1px)',
                backgroundSize: '40px 40px' 
            }}></div>

            {/* High-Fidelity SVG Motherboard */}
            <svg 
                className="absolute w-full h-full" 
                viewBox="0 0 1000 1000" 
                preserveAspectRatio="xMidYMid slice"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <filter id="circuit-glow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="1.5" result="blur" />
                        <feComposite in="SourceGraphic" in2="blur" operator="over" />
                    </filter>
                    
                    <filter id="energy-glow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="4" result="blur" />
                        <feComponentTransfer>
                            <feFuncA type="linear" slope="1.5" />
                        </feComponentTransfer>
                        <feMerge>
                            <feMergeNode />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>

                    <style>{`
                        @keyframes flow {
                            0% { stroke-dashoffset: 600; opacity: 0; }
                            5% { opacity: 1; }
                            95% { opacity: 1; }
                            100% { stroke-dashoffset: 0; opacity: 0; }
                        }
                        .trace-path {
                            stroke: #1e293b;
                            stroke-width: 1.5;
                            fill: none;
                        }
                        .energy-pulse {
                            fill: none;
                            stroke-width: 2.5;
                            stroke-dasharray: 60 540;
                            filter: url(#energy-glow);
                        }
                        .cyan-pulse { stroke: #22d3ee; animation: flow 6s linear infinite; }
                        .orange-pulse { stroke: #f97316; animation: flow 4s linear infinite; }
                        
                        @keyframes core-breathe {
                            0%, 100% { opacity: 0.2; }
                            50% { opacity: 0.6; }
                        }
                        .core-glow { animation: core-breathe 4s ease-in-out infinite; }

                        @keyframes flicker {
                            0%, 100% { opacity: 0.1; }
                            50% { opacity: 0.8; }
                            70% { opacity: 0.2; }
                            80% { opacity: 1; }
                        }
                        .flicker-cyan { fill: #22d3ee; filter: blur(2px); animation: flicker 3s ease-in-out infinite; }
                        .flicker-orange { fill: #f97316; filter: blur(2px); animation: flicker 4s ease-in-out infinite; }
                    `}</style>
                </defs>

                {/* Left Side Concentration (Motherboard Area) */}
                <g transform="translate(100, 100)">
                    {/* CPU Socket Area */}
                    <rect x="150" y="250" width="220" height="220" rx="4" fill="#0f172a" stroke="#1e293b" strokeWidth="4" />
                    
                    {/* Realistic CPU Chip Image */}
                    <image 
                        href="/cpu_chip.png" 
                        x="165" 
                        y="265" 
                        width="190" 
                        height="190" 
                        className="animate-in fade-in zoom-in-95 duration-1000"
                        style={{ filter: 'drop-shadow(0 0 15px rgba(34, 211, 238, 0.3))' }}
                    />

                    {/* Flickering Circuit Highlights on Chip Surface */}
                    <g className="chip-highlights">
                        {/* Cyan Highlights */}
                        <rect x="230" y="320" width="40" height="2" rx="1" className="flicker-cyan" />
                        <rect x="250" y="380" width="60" height="2" rx="1" className="flicker-cyan" style={{animationDelay: '1s'}} />
                        <rect x="210" y="410" width="30" height="2" rx="1" className="flicker-cyan" style={{animationDelay: '2.5s'}} />
                        
                        {/* Orange Highlights */}
                        <rect x="190" y="340" width="2" height="50" rx="1" className="flicker-orange" />
                        <rect x="320" y="300" width="2" height="40" rx="1" className="flicker-orange" style={{animationDelay: '1.5s'}} />
                        <rect x="280" y="430" width="40" height="2" rx="1" className="flicker-orange" style={{animationDelay: '0.8s'}} />
                    </g>

                    {/* Socket Pins Overlay (subtle) */}
                    <rect x="165" y="265" width="190" height="190" rx="2" fill="none" stroke="#22d3ee" strokeWidth="1" opacity="0.2" />

                    {/* Component Chips */}
                    <rect x="100" y="550" width="80" height="60" rx="2" fill="#1e293b" />
                    <rect x="420" y="200" width="100" height="40" rx="2" fill="#1e293b" />
                    <rect x="420" y="260" width="100" height="40" rx="2" fill="#1e293b" />
                    <rect x="420" y="320" width="100" height="40" rx="2" fill="#1e293b" />

                    {/* Complex Trace Network */}
                    <g className="traces">
                        {/* Original Main Buses */}
                        <path className="trace-path" d="M -100 360 L 150 360" />
                        <path className="energy-pulse cyan-pulse" d="M -100 360 L 150 360" />
                        
                        <path className="trace-path" d="M -100 400 L 150 400" />
                        <path className="energy-pulse orange-pulse" style={{animationDelay: '1s'}} d="M -100 400 L 150 400" />

                        {/* New High-Density Buses from Left */}
                        <path className="trace-path" d="M -100 380 L 150 380" />
                        <path className="energy-pulse cyan-pulse" style={{animationDelay: '0.5s', animationDuration: '3s'}} d="M -100 380 L 150 380" />
                        
                        <path className="trace-path" d="M -100 420 L 150 420" />
                        <path className="energy-pulse orange-pulse" style={{animationDelay: '2.5s', animationDuration: '5s'}} d="M -100 420 L 150 420" />

                        {/* Long Distance Buses (Going Right behind login) */}
                        <path className="trace-path" d="M 370 380 L 900 380 L 900 1000" />
                        <path className="energy-pulse cyan-pulse" style={{animationDelay: '1.2s'}} d="M 370 380 L 900 380 L 900 1000" />
                        
                        <path className="trace-path" d="M 370 420 L 800 420 L 1000 600" />
                        <path className="energy-pulse orange-pulse" style={{animationDelay: '3.8s'}} d="M 370 420 L 800 420 L 1000 600" />

                        {/* Connections To Chips & Interconnects */}
                        <path className="trace-path" d="M 370 360 L 420 360 L 420 240" />
                        <path className="energy-pulse cyan-pulse" style={{animationDelay: '2s'}} d="M 370 360 L 420 360 L 420 240" />

                        <path className="trace-path" d="M 370 400 L 550 400 L 600 500 L 600 900" />
                        <path className="energy-pulse orange-pulse" style={{animationDelay: '3s'}} d="M 370 400 L 550 400 L 600 500 L 600 900" />

                        <path className="trace-path" d="M 420 280 L 600 280 L 700 100 L 1000 100" />
                        <path className="energy-pulse cyan-pulse" style={{animationDelay: '0.8s', animationDuration: '7s'}} d="M 420 280 L 600 280 L 700 100 L 1000 100" />

                        {/* Top Peripheral Traces */}
                        <path className="trace-path" d="M 260 0 L 260 250" />
                        <path className="energy-pulse cyan-pulse" style={{animationDelay: '4s'}} d="M 260 0 L 260 250" />
                        
                        <path className="trace-path" d="M 220 0 L 220 250" />
                        <path className="energy-pulse orange-pulse" style={{animationDelay: '2.2s', animationDuration: '10s'}} d="M 220 0 L 220 250" />

                        {/* Bottom Complex Traces */}
                        <path className="trace-path" d="M 260 470 L 260 550 L 180 550" />
                        <path className="energy-pulse orange-pulse" style={{animationDelay: '5s'}} d="M 260 470 L 260 550 L 180 550" />

                        <path className="trace-path" d="M 300 470 L 300 650 L 800 650 L 800 1000" />
                        <path className="energy-pulse cyan-pulse" style={{animationDelay: '1.7s', animationDuration: '4.5s'}} d="M 300 470 L 300 650 L 800 650 L 800 1000" />

                        <path className="trace-path" d="M 150 400 L 50 400 L 0 500 L 0 800" />
                        <path className="energy-pulse cyan-pulse" style={{animationDelay: '1.5s'}} d="M 150 400 L 50 400 L 0 500 L 0 800" />
                    </g>

                    {/* Small Connector Dots */}
                    {[...Array(50)].map((_, i) => {
                        const x = Math.random() * 800 - 100;
                        const y = Math.random() * 800 - 100;
                        return <circle key={i} cx={x} cy={y} r="1.5" fill="#334155" opacity="0.3" />;
                    })}
                </g>
            </svg>

            {/* Atmosphere Glowing Orbs */}
            <div className="absolute top-1/4 left-[15%] w-[40rem] h-[40rem] bg-cyan-900/10 rounded-full blur-[100px] animate-pulse"></div>
            <div className="absolute bottom-1/4 left-[30%] w-[30rem] h-[30rem] bg-orange-950/10 rounded-full blur-[120px] animate-pulse delay-1000"></div>

            {/* Scanline Overlay */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.05)_50%)] bg-[length:100%_4px] pointer-events-none opacity-20"></div>
        </div>
    );
}
